import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { PencilSimple } from '@phosphor-icons/react';
import { PageHeader } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { APP_PATHS } from '@/constant/app-paths';
import { useUrlTab } from '@/hooks/use-linked-record';
import {
  AddressCard,
  BasicInfoCard,
  currentTermOf,
  EDIT_TABS,
  EditTab,
  EducationCard,
  EmergencyContactCard,
  Employee,
  EmployeeProfileSkeleton,
  EmployeeProfileView,
  EmployeesPagingResult,
  EmploymentCard,
  ExpertiseCard,
  FinesCard,
  HrPeriodCard,
  IdentityDocumentsCard,
  InsuranceCard,
  LeaveAllotmentCard,
  MY_PROFILE_TABS,
  MyProfileTab,
  PersonalDetailsCard,
  PrivateContactCard,
  ProfileCardGrid,
  ProfileTab,
  ReferenceCards,
  SocialProfilesCard,
  useGetMyEmployee,
  WorkingInfoCard,
} from '@/modules/hr-directory';
import { GeneralInfo } from '../../components/general-info/general-info';
import { useGetAccount } from '../../hooks/use-account';

const { OVERVIEW, CONTACT, HR } = MY_PROFILE_TABS;

/** Tab names this page used before (`location.state.tab` and old links). */
const LEGACY_TABS: Record<string, MyProfileTab> = { generalInfo: OVERVIEW, hrInfo: OVERVIEW };

const editTo = (section: EditTab) => `${APP_PATHS.MY_PROFILE_EDIT}?tab=${section}`;

/**
 * `/my-profile` (Component Map › My Profile): the Employee details layout for yourself — header with Edit
 * profile, the summary rail (last login included) and the tabs Overview / Contact info / HR terms. The
 * pencils open Edit profile on their section; the HR cards have none (HR owns them).
 */
export const ProfilePage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { data: accountInfo } = useGetAccount();
  const tabs: ProfileTab<MyProfileTab>[] = [
    { value: OVERVIEW, label: t('OVERVIEW') },
    { value: CONTACT, label: t('CONTACT_INFO') },
    { value: HR, label: t('HR_TERMS') },
  ];
  const [tab, setTab] = useUrlTab(
    tabs.map((item) => item.value),
    OVERVIEW,
    LEGACY_TABS
  );

  // A page that links back here with `state.tab` (the old way) still opens that tab.
  const legacyTab = LEGACY_TABS[(location.state as { tab?: string } | null)?.tab ?? ''];
  useEffect(() => {
    if (legacyTab) setTab(legacyTab);
    // Only on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, isLoading } = useGetMyEmployee();
  const employee: Employee | undefined = (data as { Employees?: EmployeesPagingResult })?.Employees
    ?.items?.[0];

  if (isLoading) return <EmployeeProfileSkeleton />;

  // No HR record (yet): the account settings still work.
  if (!employee) {
    return (
      <div className="flex w-full flex-col gap-4">
        <PageHeader title={t('MY_PROFILE')} />
        <GeneralInfo />
        <p className="text-sm text-muted-foreground">{t('NO_HR_RECORD_FOUND')}</p>
      </div>
    );
  }

  const term = currentTermOf(employee);
  const card = { employee };

  return (
    <EmployeeProfileView
      employee={employee}
      actions={
        <Button asChild>
          <Link to={APP_PATHS.MY_PROFILE_EDIT}>
            <PencilSimple />
            {t('EDIT_PROFILE')}
          </Link>
        </Button>
      }
      lastLoggedInTime={accountInfo?.lastLoggedInTime}
      tabs={tabs}
      tab={tab}
      onTabChange={setTab}
    >
      {tab === OVERVIEW && (
        <ProfileCardGrid>
          <EmploymentCard {...card} />
          <BasicInfoCard {...card} editTo={editTo(EDIT_TABS.OVERVIEW)} />
          <ExpertiseCard {...card} editTo={editTo(EDIT_TABS.SKILLS)} />
          <InsuranceCard {...card} />
        </ProfileCardGrid>
      )}
      {tab === CONTACT && (
        <ProfileCardGrid>
          <PrivateContactCard {...card} editTo={editTo(EDIT_TABS.CONTACT)} />
          <PersonalDetailsCard {...card} editTo={editTo(EDIT_TABS.CONTACT)} />
          <IdentityDocumentsCard {...card} editTo={editTo(EDIT_TABS.CONTACT)} />
          <EmergencyContactCard {...card} editTo={editTo(EDIT_TABS.CONTACT)} />
          <AddressCard {...card} editTo={editTo(EDIT_TABS.CONTACT)} />
          <EducationCard {...card} editTo={editTo(EDIT_TABS.CONTACT)} />
          <SocialProfilesCard {...card} editTo={editTo(EDIT_TABS.CONTACT)} />
          <ReferenceCards {...card} editTo={editTo(EDIT_TABS.CONTACT)} />
        </ProfileCardGrid>
      )}
      {tab === HR &&
        (term ? (
          <ProfileCardGrid>
            <HrPeriodCard {...card} term={term} />
            <WorkingInfoCard {...card} term={term} />
            <LeaveAllotmentCard {...card} term={term} />
            <FinesCard {...card} term={term} />
          </ProfileCardGrid>
        ) : (
          <p className="text-sm text-muted-foreground">{t('NO_CURRENT_HR_TERM')}</p>
        ))}
    </EmployeeProfileView>
  );
};
